import React from 'react';
import classNames from 'classnames/bind';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Controller, useForm, Validate } from 'react-hook-form';

import { useSpringState } from 'hooks/useSpringState';
import { isAPIError } from 'types/api_error';
import { organizationPlansPath } from 'application/types/routes';
import { useAPI } from 'hooks/api/useAPI';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { formatUtilizationLimit } from 'organizations/utils/utilizationLimit';

import { AppLink } from 'components/AppLink/AppLink';
import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Meter, MeterValue } from 'components/Meter';
import {
  NumberInput,
  NumberInputProps,
} from 'components/NumberInput/NumberInput';
import { Text } from 'components/Text/Text';
import * as Dialog from 'components/Dialog';

import styles from './UtilizationLimitDialog.module.scss';

const c = classNames.bind(styles);

export type UtilizationLimitDialogProps = {
  trigger: JSX.Element;
  item: string;

  usage: number;
  effectiveLimit: number | null | undefined;
  planLimit: number | null | undefined;
  userLimit: number | null | undefined;
  includedInPlanInfo: string;
  currentBillingItems?: { label: string; value: string }[];
  limitToggleDescription?: React.ReactNode;

  unit?: 'filesize' | string;
  limitUnit?: string;
  step?: number;
  limitHelpText?: string;
  isLoading?: boolean;
  isSuccess?: boolean;

  InputComponent?: React.FunctionComponent<NumberInputProps>;

  onSave: (values: UtilizationLimitFieldValues) => Promise<number | null>;
  validate?:
    | Validate<number | '' | undefined, UtilizationLimitFieldValues>
    | Record<
        string,
        Validate<number | '' | undefined, UtilizationLimitFieldValues>
      >;
};

export type UtilizationLimitFieldValues = {
  shouldLimitUtilization: boolean;
  utilizationLimit?: number | '';
};

function getValue(value: number | null | undefined | '') {
  return Number.isFinite(value) ? Number(value) : '';
}

function getSaveErrorMessage(error: unknown) {
  if (isAPIError(error)) {
    const contextDetails = error.context?.details;

    if (typeof contextDetails === 'string') {
      return contextDetails;
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to update utilization limits.';
}

export function UtilizationLimitDialog({
  trigger,
  item,
  usage,
  effectiveLimit,
  planLimit,
  userLimit,
  includedInPlanInfo,
  currentBillingItems,
  limitToggleDescription,
  unit,
  limitUnit,
  step,
  limitHelpText,
  isLoading,
  isSuccess,
  InputComponent,
  validate,
  onSave,
}: UtilizationLimitDialogProps) {
  const { applicationID } = useAPI();
  const { data: organization } = useCurrentOrganization();
  const {
    control,
    formState: { errors },
    register,
    watch,
    handleSubmit,
    reset,
  } = useForm<UtilizationLimitFieldValues>({
    defaultValues: {
      shouldLimitUtilization: Number.isFinite(userLimit),
      utilizationLimit: getValue(userLimit),
    },
    mode: 'onSubmit',
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [didSave, setDidSave] = useSpringState(false, 2000);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const shouldLimitUtilization = watch('shouldLimitUtilization');

  function resetFormToCurrentLimit() {
    reset({
      shouldLimitUtilization: Number.isFinite(userLimit),
      utilizationLimit: getValue(userLimit),
    });
    setSaveError(null);
    setDidSave(false);
  }

  function handleOpenChange(open: boolean) {
    if (open) {
      resetFormToCurrentLimit();
    }

    setIsOpen(open);
  }

  React.useEffect(() => {
    if (!isSuccess) {
      return;
    }

    setDidSave(isSuccess);
  }, [isSuccess, setDidSave]);

  React.useEffect(() => {
    if (!didSave) {
      return;
    }

    return () => {
      setIsOpen(false);
    };
  }, [didSave]);

  const billingItems = currentBillingItems ?? [
    { label: 'Included in plan', value: includedInPlanInfo },
    {
      label: 'Plan limit',
      value: formatUtilizationLimit(planLimit, unit, limitUnit),
    },
  ];

  const toggleDescription = limitToggleDescription ?? (
    <>
      Overage pricing according to your selected plan.
      {organization && (
        <>
          {' '}
          <AppLink
            to={organizationPlansPath(applicationID, organization.id)}
            className={c('plans-link')}
          >
            View your plan
          </AppLink>
        </>
      )}
    </>
  );

  async function onSubmit({
    shouldLimitUtilization,
    utilizationLimit,
  }: UtilizationLimitFieldValues) {
    setSaveError(null);
    setDidSave(false);

    try {
      const updatedUtilizationLimit = await onSave({
        shouldLimitUtilization,
        utilizationLimit,
      });

      reset({
        shouldLimitUtilization: Number.isFinite(updatedUtilizationLimit),
        utilizationLimit: getValue(updatedUtilizationLimit),
      });
    } catch (error) {
      setSaveError(getSaveErrorMessage(error));
    }
  }

  return (
    <Dialog.Root
      className={c('dialog')}
      trigger={trigger}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Dialog.Title>
        <Heading level="3" asChild>
          <span>{item} utilization</span>
        </Heading>
      </Dialog.Title>

      {didSave && (
        <Dialog.Message intent="success">
          Utilization limits updated!
        </Dialog.Message>
      )}

      {saveError && (
        <FormMessage icon="warning" intent="danger">
          {saveError}
        </FormMessage>
      )}

      <div className={c('meter')}>
        <MeterValue value={usage} max={effectiveLimit} unit={unit} />
        {Number.isFinite(effectiveLimit) && (
          <Meter value={usage} max={Number(effectiveLimit)} />
        )}
      </div>

      <dl className={c('current-billing')}>
        {billingItems.map(({ label, value }) => (
          <div key={label} className={c('current-billing-item')}>
            <dt className="input-label">{label}</dt>
            <Heading level="3" asChild>
              <dd>{value}</dd>
            </Heading>
          </div>
        ))}
      </dl>

      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <div className={c('toggle')}>
            <LightSwitch {...register('shouldLimitUtilization')} />
            <div>
              <label htmlFor="max-limit-toggle">
                <p className="input-label">Adjust usage limits</p>
              </label>
              <Text>{toggleDescription}</Text>
            </div>
          </div>

          {shouldLimitUtilization && (
            <div
              className={c('toggle-input', { error: errors.utilizationLimit })}
            >
              <VisuallyHidden>
                <label htmlFor="max-limit">
                  Maximum utilization {limitUnit && `in ${limitUnit}`}
                </label>
              </VisuallyHidden>
              <Controller
                name="utilizationLimit"
                control={control}
                render={({ field }) => {
                  if (InputComponent) {
                    return (
                      <InputComponent
                        {...field}
                        id="max-limit"
                        unit={limitUnit}
                        min={0}
                        step={step}
                      />
                    );
                  }

                  return (
                    <NumberInput
                      {...field}
                      id="max-limit"
                      unit={limitUnit}
                      min={0}
                      step={step}
                    />
                  );
                }}
                rules={{
                  required: 'Please enter a utilization limit.',
                  min: {
                    value: 0,
                    message:
                      'Please enter a number greater than or equal to 0.',
                  },
                  validate,
                }}
              />
              {errors.utilizationLimit && (
                <Text intent="danger">{errors.utilizationLimit.message}</Text>
              )}

              {limitHelpText && !errors.utilizationLimit && (
                <Text className={c('input-help')} size="small">
                  {limitHelpText}
                </Text>
              )}
            </div>
          )}
        </fieldset>

        <ButtonGroup>
          <Button type="submit" variant="primary" loading={isLoading}>
            Save changes
          </Button>

          <Dialog.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Dialog.Close>
        </ButtonGroup>
      </form>
    </Dialog.Root>
  );
}
